import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Download, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatApiError, formatShort } from "../lib/format";

const KINDS = ["CRAF","Guia de Tráfego","Porte","Registro","Certidão","Nota Fiscal","Outro"];

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [clients, setClients] = useState([]);
  const [weapons, setWeapons] = useState([]);
  const [selClient, setSelClient] = useState("");
  const [selWeapon, setSelWeapon] = useState("");
  const [selKind, setSelKind] = useState(KINDS[0]);
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [d, c, w] = await Promise.all([api.get("/documents"), api.get("/clients"), api.get("/weapons")]);
      setDocuments(d.data); setClients(c.data); setWeapons(w.data);
      if (!selClient && c.data.length) setSelClient(c.data[0].id);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const clientWeapons = weapons.filter(w => w.client_id === selClient);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Escolha um arquivo"); return; }
    if (!selClient) { toast.error("Selecione um cliente"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("client_id", selClient);
      fd.append("kind", selKind);
      if (selWeapon) fd.append("weapon_id", selWeapon);
      if (expires) fd.append("expires_at", expires);
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Documento enviado");
      if (fileRef.current) fileRef.current.value = "";
      setExpires("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = d.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Excluir este documento?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Documento removido"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const cmap = Object.fromEntries(clients.map(c => [c.id, c.name]));

  return (
    <>
      <div className="page-heading">
        <div>
          <span className="eyebrow">ARQUIVOS</span>
          <h1>Documentos</h1>
          <p>Guarde e disponibilize CRAFs, guias e certidões dos seus clientes.</p>
        </div>
      </div>

      <section className="panel">
        <div className="panel-head"><div><span className="eyebrow">ENVIO SEGURO</span><h2>Enviar novo documento</h2></div></div>
        <div className="upload-grid">
          <label>Cliente
            <select data-testid="doc-client-select" value={selClient} onChange={e=>{setSelClient(e.target.value);setSelWeapon("");}}>
              <option value="">Selecione...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label>Arma (opcional)
            <select data-testid="doc-weapon-select" value={selWeapon} onChange={e=>setSelWeapon(e.target.value)}>
              <option value="">— nenhuma —</option>
              {clientWeapons.map(w => <option key={w.id} value={w.id}>{w.model}</option>)}
            </select>
          </label>
          <label>Tipo de documento
            <select data-testid="doc-kind-select" value={selKind} onChange={e=>setSelKind(e.target.value)}>
              {KINDS.map(k => <option key={k}>{k}</option>)}
            </select>
          </label>
          <label>Validade (opcional)<input type="date" data-testid="doc-expires-input" value={expires} onChange={e=>setExpires(e.target.value)}/></label>
          <label className="span-2">Arquivo (PDF, imagens até 10MB)<input type="file" ref={fileRef} data-testid="doc-file-input" accept=".pdf,image/*"/></label>
        </div>
        <button data-testid="upload-doc-button" className="primary-button" disabled={busy} onClick={upload}>
          <Upload size={16}/>{busy?"Enviando...":"Enviar documento"}
        </button>
      </section>

      <section className="panel">
        <div className="panel-head"><div><span className="eyebrow">ACERVO</span><h2>Documentos cadastrados</h2></div></div>
        <div className="doc-list">
          {documents.map(d => (
            <div className="doc-row" data-testid={`doc-row-${d.id}`} key={d.id}>
              <div className="doc-icon"><FileText size={18}/></div>
              <div className="doc-info">
                <b>{d.filename}</b>
                <span>{cmap[d.client_id] || "—"} · {d.kind} {d.expires_at && `· vence ${formatShort(d.expires_at)}`}</span>
              </div>
              <span className="doc-size">{Math.round((d.size||0)/1024)} KB</span>
              <div className="row-actions">
                <button data-testid={`download-doc-${d.id}`} className="icon-mini" onClick={()=>download(d)}><Download size={14}/></button>
                <button data-testid={`delete-doc-${d.id}`} className="icon-mini danger" onClick={()=>remove(d.id)}><Trash2 size={14}/></button>
              </div>
            </div>
          ))}
          {!documents.length && <div className="empty-inline">Nenhum documento enviado ainda.</div>}
        </div>
      </section>
    </>
  );
}
